import type {
  MessageDetail,
  MessageStatus,
  QueueActionKind,
  QueueActionOutcome,
  QueueActionResult,
  RetryPreview,
  SendNowPreview
} from "../types/messages.types.ts"

/** What a list row or the reading pane needs to offer Send now or Retry. */
export interface QueueActionTarget {
  id: string
  status: MessageStatus
  channel: "email" | "sms"
  to_address: string
  contact_name?: string
  failure_reason?: string
  actions?: { send_now: boolean; retry: boolean }
}

/** While a sent-now or retried message is still on its way, the pane asks again this often. */
export const OUTCOME_POLL_MS = 2_000
export const OUTCOME_POLL_LIMIT = 10

/** A message that has not reached a provider's answer yet. */
export const STILL_GOING = new Set<MessageStatus>(["queued", "sending"])

export const offered = (
  target: Pick<QueueActionTarget, "status" | "actions">
): QueueActionKind[] => {
  const actions: QueueActionKind[] = []
  if (target.actions?.send_now && target.status === "queued")
    actions.push("send_now")
  if (target.actions?.retry && target.status === "failed") actions.push("retry")
  return actions
}

/** A POST success arrives in the envelope (`details.data`); a 409 refusal arrives bare. */
export const unwrapResult = (data: unknown): QueueActionResult | null => {
  if (!data || typeof data !== "object") return null
  const inner = (data as { details?: { data?: unknown } }).details?.data
  const body = (inner ?? data) as Partial<QueueActionResult>
  return body && typeof body.outcome === "string"
    ? (body as QueueActionResult)
    : null
}

export const requestBody = (
  action: QueueActionKind,
  confirmed: boolean
): { confirm_outside_hours: boolean } | { confirm_not_received: boolean } =>
  action === "send_now"
    ? { confirm_outside_hours: confirmed }
    : { confirm_not_received: confirmed }

/** Whether the confirm button may be pressed for what the server said. */
export const canConfirm = (
  preview: SendNowPreview | RetryPreview,
  confirmed: boolean
) => {
  if (preview.action === "send_now") {
    if (!preview.can_send || preview.state) return false
    return !preview.override.needed || confirmed
  }
  if (!preview.can_retry || preview.not_retryable_reason) return false
  return !preview.uncertain || confirmed
}

/** The outcomes after which the dialog stays open, because the person can still decide. */
export const REFUSALS = new Set<QueueActionOutcome>([
  "blocked",
  "override_required",
  "confirmation_required",
  "not_retryable"
])

/** How a finished delivery reads once the worker has answered. */
export const settledTone = (
  message: MessageDetail
): "success" | "error" | "warning" | "info" => {
  if (message.status === "sent" || message.status === "delivered")
    return "success"
  if (["failed", "bounced", "complained"].includes(message.status))
    return "error"
  if (message.status === "skipped") return "warning"
  return "info"
}
